import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { KanbanCardBodyComponent } from '@digiwall/goliath-core';
import { PlanningDataSource } from '@digiwall/goliath-documents/proxy';
import { GoliathDocumentsEnumerationTypeStore } from '../../../enumeration-types/enumeration-type.store';
import { BasePlanningCardComponent } from '../base-planning-card.component';
import { PlanningDesiredDateComponent } from '../../planning-desired-date.component';
import { ProductionFolderPlanningItemFieldsDto } from './production-folder-planning-item-fields';
import { LocalizationModule } from '@abp/ng.core';

/**
 * Item card for the ProductionFolder planning data source. Rendered by the board through
 * NgComponentOutlet with only the generic item dto; the ProductionFolder specifics are read from the
 * typed `displayFields` payload. Content is fully custom (projected into `core-kanban-card-body`); the
 * wrapper supplies the shared reference/heading/status-dot chrome, resolving the status from the store's
 * workOrderStatus values + the item's `statusId` (the same colour the board draws as the side border).
 */
@Component({
  selector: 'document-planning-production-folder-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe],
  imports: [KanbanCardBodyComponent, LocalizationModule, PlanningDesiredDateComponent],
  template: `
    <core-kanban-card-body
      [reference]="fields().displayNumber"
      [heading]="fields().customer"
      [statusEnum]="store.workOrderStatus()"
      [status]="dto().statusId"
      [statusEditable]="statusEditable()"
      [selectableStatuses]="store.workOrderStatus()"
      [statusItemId]="statusItemId()"
    >
      @if (isVisible('manufacturer') && fields().manufacturer; as manufacturer) {
        <div class="kanban-card-body__line">
          <span class="kanban-card-body__line-label">{{ '::Manufacturer' | abpLocalization }}</span>
          <span class="kanban-card-body__line-value">{{ manufacturer }}</span>
        </div>
      }
      @if (isVisible('responsible') && fields().responsible; as responsible) {
        <div class="kanban-card-body__line">
          <span class="kanban-card-body__line-label">{{ '::Responsible' | abpLocalization }}</span>
          <span class="kanban-card-body__line-value">{{ responsible }}</span>
        </div>
      }
      @if (isVisible('schedule') && scheduleLabel(); as scheduleLabel) {
        <div class="kanban-card-body__line">
          <span class="kanban-card-body__line-label">{{
            'Doc::EndProdDate' | abpLocalization
          }}</span>
          <span class="kanban-card-body__line-value">{{ scheduleLabel }}</span>
        </div>
      }
      @if (dto().requestedDate) {
        <div class="kanban-card-body__line d-flex">
          <document-planning-desired-date
            class="ms-auto"
            [date]="dto().requestedDate"
            [critical]="requestedDateCritical()"
          />
        </div>
      }
    </core-kanban-card-body>
  `,
})
export class ProductionFolderPlanningCardComponent extends BasePlanningCardComponent<ProductionFolderPlanningItemFieldsDto> {
  protected readonly store = inject(GoliathDocumentsEnumerationTypeStore);
  private readonly datePipe = inject(DatePipe);
  protected readonly dataSource = PlanningDataSource.Production;

  protected readonly scheduleLabel = computed(() => {
    const fields = this.fields();
    const date = fields.preferredProductionDate
      ? (this.datePipe.transform(fields.preferredProductionDate, 'd MMM y') ?? undefined)
      : undefined;
    return date || undefined;
  });
}
